import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import {
  HiArrowDownTray,
  HiArrowRight,
  HiFolder,
  HiMagnifyingGlass,
  HiPhoto,
  HiShoppingCart,
  HiSquares2X2,
  HiUserCircle,
  HiWallet,
} from "react-icons/hi2";
import apiClient from "../api/apiClient";
import RecentPurchasesList from "../components/RecentPurchasesList";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import {
  DashboardHero,
  ErrorState,
  Panel,
  RangeTabs,
  StatTile,
  TileSkeleton,
  TrendChart,
  bucketLabel,
  bucketTitle,
  formatCount,
  formatCountTick,
  formatMoney,
  formatMoneyTick,
  previousLabel,
  toRows,
  useActivity,
} from "../components/dashboard-kit";

const QUICK_LINKS = [
  { label: "My Mockups", description: "Open and edit your saved mockups", to: "/user/my-mockups", icon: HiPhoto },
  { label: "Purchases", description: "Your full purchase history", to: "/user/payments", icon: HiShoppingCart },
  { label: "My Downloads", description: "Re-download files from the editor", to: "/user/my-downloads", icon: HiArrowDownTray },
  { label: "Account", description: "Profile, password and preferences", to: "/user/info", icon: HiUserCircle },
];

const formatDownloadDate = (dateStr) => {
  const date = dateStr ? new Date(dateStr) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function DownloadCard({ item }) {
  const [imageError, setImageError] = useState(false);
  return (
    <div className="dk-gallery__item">
      <div className="dk-gallery__media">
        {item?.image && !imageError ? (
          <img src={item.image} alt={item?.listing_title || "Recent download"} loading="lazy" onError={() => setImageError(true)} />
        ) : (
          <ImageNotSupportedOutlinedIcon />
        )}
      </div>
      <p className="dk-gallery__title" title={item?.listing_title}>
        {item?.listing_title || "Download"}
      </p>
      <p className="dk-gallery__date">{formatDownloadDate(item?.created_at)}</p>
    </div>
  );
}

const UserDashboard = () => {
  const { profiledata } = useIndexPageData();
  const [range, setRange] = useState("30d");
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const activity = useActivity("/stats/user-activity/", range);

  const fetchStats = useCallback(async () => {
    setStatsError(null);
    const result = await apiClient.get("/stats/user/");
    if (!result?.ok) {
      setStatsError("Unable to load dashboard data.");
      return;
    }
    setStats(result.data);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const summary = stats?.summary ?? {};
  const recentPurchases = stats?.recent_purchases ?? [];
  const recentDownloads = stats?.recent_downloads ?? [];
  const userName = profiledata?.full_name?.trim();

  const { data, loading, refreshing, error, reload } = activity;
  const money = (v) => formatMoney(v, data?.currency || "USD");
  const rows = useMemo(() => toRows(data, bucketLabel, bucketTitle), [data]);
  const totals = data?.totals || {};
  const previous = data?.previous || {};
  const prev = previousLabel(range);

  return (
    <div className="dk-page dk-stack">
      <DashboardHero
        eyebrow="Your dashboard"
        name={userName}
        subtitle="Your mockups, purchases and creations — all in one place. Ready to make something new?"
        actions={
          <>
            <Link to="/Searchpage" className="dk-btn dk-btn--light">
              <HiMagnifyingGlass aria-hidden="true" /> Browse mockups
            </Link>
            <Link to="/user/my-mockups" className="dk-btn dk-btn--glass">
              <HiSquares2X2 aria-hidden="true" /> My mockups
            </Link>
          </>
        }
        figureLabel="Mockups & bundles purchased"
        figure={stats ? formatCount(summary.total_purchases ?? 0) : "—"}
        figureNote={
          stats ? (
            <>
              {formatCount(summary.total_downloads ?? 0)} design{summary.total_downloads === 1 ? "" : "s"} downloaded · {formatMoney(summary.total_spent ?? 0)} spent in total
            </>
          ) : null
        }
      />

      {statsError && <ErrorState message={statsError} onRetry={fetchStats} />}

      <div className="dk-links">
        {QUICK_LINKS.map(({ label, description, to, icon: Icon }, i) => (
          <Link key={to} to={to} className="dk-linkcard dk-rise" style={{ "--i": i + 1 }}>
            <span className="dk-linkcard__icon">
              <Icon size={22} aria-hidden="true" />
            </span>
            <span>
              <span className="dk-linkcard__title d-block">{label}</span>
              <span className="dk-linkcard__text d-block">{description}</span>
            </span>
            <HiArrowRight className="dk-linkcard__arrow" aria-hidden="true" size={18} />
          </Link>
        ))}
      </div>

      <div className="dk-toolbar dk-rise" style={{ "--i": 5 }}>
        <div>
          <h2 className="dk-toolbar__title">Your activity</h2>
          <p className="dk-toolbar__sub">What you've bought and created over time.</p>
        </div>
        <RangeTabs value={range} onChange={setRange} />
      </div>

      {error && !data && <ErrorState message={error} onRetry={reload} />}
      {loading && !data ? (
        <TileSkeleton count={3} />
      ) : (
        data && (
          <div className="dk-tiles">
            <StatTile index={6} label="Mockups purchased" icon={HiShoppingCart} value={totals.purchases} format={formatCount} current={totals.purchases} previous={previous.purchases} previousLabel={prev} trend={data.series.purchases} />
            <StatTile index={7} label="Designs downloaded" icon={HiArrowDownTray} value={totals.downloads} format={formatCount} current={totals.downloads} previous={previous.downloads} previousLabel={prev} trend={data.series.downloads} />
            <StatTile index={8} label="Spent" icon={HiWallet} value={totals.spent} format={money} current={totals.spent} previous={previous.spent} previousLabel={prev} goodWhen="neutral" trend={data.series.spent} />
            <StatTile index={9} label="Folders" icon={HiFolder} value={summary.total_folders ?? 0} format={formatCount} hint="Organise your library into folders" />
          </div>
        )
      )}

      <div className="dk-grid">
        <Panel className="dk-span-7" index={10} title="Designs & purchases" subtitle="Downloads from the editor and mockups you bought">
          <TrendChart
            rows={rows}
            refreshing={refreshing}
            series={[
              { key: "downloads", label: "Downloads", kind: "bar" },
              { key: "purchases", label: "Purchases", kind: "bar" },
            ]}
            formatValue={formatCount}
            formatTick={formatCountTick}
            integer
            caption="Downloads and purchases by period"
            emptyText="Nothing here yet — open a mockup in the editor to create your first design."
          />
        </Panel>
        <Panel className="dk-span-5" index={11} title="Spending" subtitle="What you paid, after discounts">
          <TrendChart
            rows={rows}
            refreshing={refreshing}
            series={[{ key: "spent", label: "Spent", kind: "area" }]}
            formatValue={money}
            formatTick={(v) => formatMoneyTick(v, data?.currency || "USD")}
            caption="Spending by period"
            emptyText="No purchases in this period."
          />
        </Panel>

        <Panel
          className="dk-span-12"
          index={12}
          title="Recent downloads"
          action={
            recentDownloads.length > 0 && (
              <Link to="/user/my-downloads" className="dk-link">
                View all
              </Link>
            )
          }
        >
          {recentDownloads.length ? (
            <div className="dk-gallery">
              {recentDownloads.map((item) => (
                <DownloadCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <p className="dk-empty">No downloads yet. Edit a mockup and download it to see it here.</p>
          )}
        </Panel>

        <Panel
          className="dk-span-12"
          index={13}
          title="Recent purchases"
          action={
            recentPurchases.length > 0 && (
              <Link to="/user/payments" className="dk-link">
                View all
              </Link>
            )
          }
        >
          <RecentPurchasesList items={recentPurchases} emptyMessage="You haven't purchased any mockups yet." />
        </Panel>
      </div>
    </div>
  );
};

export default UserDashboard;
